import { useState } from 'react';

export interface Story {
  id: string;
  region: string;
  category: string;
  title: string;
  excerpt: string;
  cover: string;
  date: string;
  readTime: number;
  views: string;
  author?: string;
  tags?: string[];
}

export default function StoryCard({ story, featured = false }: { story: Story; featured?: boolean }) {
  const [saved, setSaved] = useState(false);

  if (featured) {
    return (
      <article className="group grid grid-cols-1 overflow-hidden rounded-lg border border-foreground-950/10 bg-background-50 lg:grid-cols-2">
        <div className="relative h-64 overflow-hidden lg:h-full lg:min-h-[380px]">
          <img
            src={story.cover}
            alt={`${story.title} 配图`}
            title={`${story.title} · 美日吃瓜`}
            className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
          />
          <span className="absolute left-4 top-4 rounded-md bg-primary-500 px-3 py-1 text-xs font-medium text-background-50">
            {story.region} · {story.category}
          </span>
        </div>
        <div className="flex flex-col justify-center p-6 md:p-10">
          <div className="flex items-center gap-4 text-xs text-foreground-500">
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-time-line"></i>
              {story.date}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-eye-line"></i>
              {story.views} 阅读
            </span>
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-book-open-line"></i>
              {story.readTime} 分钟
            </span>
          </div>
          <h3 className="mt-5 font-heading text-xl font-bold leading-relaxed text-foreground-950 md:text-3xl">
            {story.title}
          </h3>
          <p className="mt-4 text-sm leading-8 text-foreground-600 md:text-base">{story.excerpt}</p>
          {story.tags && (
            <div className="mt-6 flex flex-wrap gap-2">
              {story.tags.map((tag) => (
                <span key={tag} className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900">
                  #{tag}
                </span>
              ))}
            </div>
          )}
          <div className="mt-8 flex items-center justify-between border-t border-foreground-950/10 pt-5">
            <span className="text-xs text-foreground-500">文字 · {story.author}</span>
            <button
              type="button"
              onClick={() => setSaved((v) => !v)}
              aria-pressed={saved}
              className={`inline-flex items-center gap-2 whitespace-nowrap rounded-md border px-4 py-2 text-xs transition-colors ${
                saved
                  ? 'border-primary-300 bg-primary-100 text-primary-700'
                  : 'border-foreground-950/15 text-foreground-600 hover:border-primary-300 hover:text-primary-600'
              }`}
            >
              <i className={saved ? 'ri-bookmark-fill' : 'ri-bookmark-line'}></i>
              {saved ? '已加入稍后读' : '稍后读'}
            </button>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex flex-col overflow-hidden rounded-lg border border-foreground-950/10 bg-background-50 transition-colors hover:border-primary-200">
      <div className="relative h-48 overflow-hidden">
        <img
          src={story.cover}
          alt={`${story.title} 配图`}
          title={`${story.title} · 美日吃瓜`}
          className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
        />
        <span className="absolute left-3 top-3 rounded-md bg-background-50/90 px-2.5 py-1 text-xs font-medium text-foreground-900 backdrop-blur">
          {story.category}
        </span>
        <button
          type="button"
          onClick={() => setSaved((v) => !v)}
          aria-label={saved ? '取消稍后读' : '加入稍后读'}
          aria-pressed={saved}
          className={`absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-md backdrop-blur transition-colors ${
            saved ? 'bg-primary-500 text-background-50' : 'bg-background-50/90 text-foreground-700 hover:text-primary-600'
          }`}
        >
          <i className={saved ? 'ri-bookmark-fill' : 'ri-bookmark-line'}></i>
        </button>
      </div>
      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-center gap-3 text-xs text-foreground-500">
          <span className="rounded bg-accent-100 px-2 py-0.5 font-medium text-accent-800">{story.region}</span>
          <span>{story.date}</span>
        </div>
        <h3 className="mt-3 font-heading text-base font-bold leading-relaxed text-foreground-950 transition-colors group-hover:text-primary-600 md:text-lg">
          {story.title}
        </h3>
        <p className="mt-3 flex-1 text-sm leading-7 text-foreground-600">{story.excerpt}</p>
        <div className="mt-4 flex items-center justify-between border-t border-foreground-950/10 pt-4 text-xs text-foreground-500">
          <span className="inline-flex items-center gap-1.5">
            <i className="ri-eye-line"></i>
            {story.views}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <i className="ri-book-open-line"></i>
            {story.readTime} 分钟
          </span>
        </div>
      </div>
    </article>
  );
}